import React, { useState, useMemo } from 'react';
import {
  Users,
  Plus,
  Search,
  Mail,
  Shield,
  UserCheck,
  UserX,
  Edit,
  Trash2,
  RefreshCw,
  Check,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '@/contexts/AuthContext';
import { usePageTour, WelcomeModal } from '@/walkthrough';
import { usersPageTour } from '@/walkthrough/tourSteps';
import {
  useUsers,
  useUpdateUser,
  useActivateUser,
  useDeactivateUser,
  useDeleteUser,
  useInviteUser,
} from '@/hooks/useUsers';
import type { User, UserRole, UserStatus } from '@/types';

// ===========================================
// Types
// ===========================================

interface EditingUser {
  id: string;
  displayName: string;
  role: UserRole;
}

// ===========================================
// Main Component
// ===========================================

export function UsersPage() {
  const { user: currentUser, isOwner } = useAuth();
  const { data: users, isLoading } = useUsers();
  const { showWelcomeModal, startTour, skipTour, globalOptOut } = usePageTour(usersPageTour);
  const updateUserMutation = useUpdateUser();
  const activateUserMutation = useActivateUser();
  const deactivateUserMutation = useDeactivateUser();
  const deleteUserMutation = useDeleteUser();

  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<UserRole | ''>('');
  const [statusFilter, setStatusFilter] = useState<UserStatus | ''>('');
  const [editingUser, setEditingUser] = useState<EditingUser | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);

  // Filter users
  const filteredUsers = useMemo(() => {
    if (!users) return [];

    return users.filter((user) => {
      const matchesSearch =
        !searchQuery ||
        user.displayName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.email.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesRole = !roleFilter || user.role === roleFilter;
      const matchesStatus = !statusFilter || user.status === statusFilter;

      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [users, searchQuery, roleFilter, statusFilter]);

  const handleEdit = (user: User) => {
    setEditingUser({
      id: user.id,
      displayName: user.displayName,
      role: user.role,
    });
  };

  const handleSaveEdit = async () => {
    if (!editingUser) return;

    try {
      await updateUserMutation.mutateAsync({
        id: editingUser.id,
        updates: {
          displayName: editingUser.displayName,
          role: editingUser.role,
        },
      });
      setEditingUser(null);
    } catch {
      // Error handled by mutation
    }
  };

  const handleToggleStatus = async (user: User) => {
    if (user.status === 'active') {
      await deactivateUserMutation.mutateAsync(user.id);
    } else {
      await activateUserMutation.mutateAsync(user.id);
    }
  };

  const handleDelete = async (userId: string) => {
    try {
      await deleteUserMutation.mutateAsync(userId);
      setDeleteConfirmId(null);
    } catch {
      // Error handled by mutation
    }
  };

  const getRoleBadgeClass = (role: UserRole) => {
    switch (role) {
      case 'owner':
        return 'bg-purple-100 text-purple-800';
      case 'admin':
        return 'bg-blue-100 text-blue-800';
      case 'rep':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusBadgeClass = (status: UserStatus) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">Users & Reps</h1>
          <p className="page-description">
            Manage user accounts and roles.
          </p>
        </div>
        <button
          onClick={() => setShowInviteModal(true)}
          data-tour="invite-user"
          className="btn btn-primary"
        >
          <Plus size={20} />
          Invite User
        </button>
      </div>

      {/* Search and filters */}
      <div className="card">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search users by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input pl-10"
            />
          </div>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value as UserRole | '')}
            className="input sm:w-40"
          >
            <option value="">All Roles</option>
            <option value="owner">Owner</option>
            <option value="admin">Admin</option>
            <option value="rep">Rep</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as UserStatus | '')}
            className="input sm:w-40"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="pending">Pending</option>
          </select>
        </div>
      </div>

      {/* Users table */}
      {isLoading ? (
        <div className="card">
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="animate-spin text-blue-600" size={32} />
          </div>
        </div>
      ) : filteredUsers.length > 0 ? (
        <div data-tour="users-table" className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                <th>Status</th>
                <th>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr key={user.id}>
                  <td>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-medium">
                        {user.displayName?.charAt(0) || 'U'}
                      </div>
                      <div>
                        {editingUser?.id === user.id ? (
                          <input
                            type="text"
                            value={editingUser.displayName}
                            onChange={(e) =>
                              setEditingUser({ ...editingUser, displayName: e.target.value })
                            }
                            className="input py-1"
                          />
                        ) : (
                          <>
                            <div className="font-medium text-gray-900">{user.displayName}</div>
                            <div className="text-sm text-gray-500">{user.email}</div>
                          </>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    {editingUser?.id === user.id ? (
                      <select
                        value={editingUser.role}
                        onChange={(e) =>
                          setEditingUser({ ...editingUser, role: e.target.value as UserRole })
                        }
                        className="input py-1"
                        disabled={user.role === 'owner' && !isOwner}
                      >
                        {isOwner && <option value="owner">Owner</option>}
                        <option value="admin">Admin</option>
                        <option value="rep">Rep</option>
                      </select>
                    ) : (
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getRoleBadgeClass(user.role)}`}>
                        {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                      </span>
                    )}
                  </td>
                  <td>
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeClass(user.status)}`}>
                      {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                    </span>
                  </td>
                  <td className="text-sm text-gray-500">
                    {user.createdAt.toLocaleDateString()}
                  </td>
                  <td>
                    {editingUser?.id === user.id ? (
                      <div className="flex gap-1">
                        <button
                          onClick={handleSaveEdit}
                          disabled={updateUserMutation.isPending}
                          className="p-2 text-green-600 hover:bg-green-50 rounded"
                        >
                          <Check size={18} />
                        </button>
                        <button
                          onClick={() => setEditingUser(null)}
                          className="p-2 text-gray-500 hover:bg-gray-100 rounded"
                        >
                          <X size={18} />
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-1">
                        {user.id !== currentUser?.id && (
                          <>
                            <button
                              onClick={() => handleEdit(user)}
                              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-gray-100 rounded"
                              title="Edit user"
                            >
                              <Edit size={18} />
                            </button>
                            <button
                              onClick={() => handleToggleStatus(user)}
                              className={`p-2 hover:bg-gray-100 rounded ${
                                user.status === 'active'
                                  ? 'text-gray-500 hover:text-orange-600'
                                  : 'text-gray-500 hover:text-green-600'
                              }`}
                              title={user.status === 'active' ? 'Deactivate' : 'Activate'}
                            >
                              {user.status === 'active' ? (
                                <UserX size={18} />
                              ) : (
                                <UserCheck size={18} />
                              )}
                            </button>
                            {isOwner && user.role !== 'owner' && (
                              <button
                                onClick={() => setDeleteConfirmId(user.id)}
                                className="p-2 text-gray-500 hover:text-red-600 hover:bg-gray-100 rounded"
                                title="Delete user"
                              >
                                <Trash2 size={18} />
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : users && users.length > 0 ? (
        <div className="card">
          <div className="empty-state">
            <Search size={48} className="empty-state-icon" />
            <p className="text-lg font-medium text-gray-900">No matching users</p>
            <p className="mt-1">Try adjusting your search or filters.</p>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="empty-state">
            <Users size={48} className="empty-state-icon" />
            <p className="text-lg font-medium text-gray-900">No users yet</p>
            <p className="mt-1 mb-4">
              Get started by inviting your first team member.
            </p>
            <button
              onClick={() => setShowInviteModal(true)}
              className="btn btn-primary"
            >
              <Mail size={20} />
              Send Invitation
            </button>
          </div>
        </div>
      )}

      {/* Role legend */}
      <div data-tour="role-legend" className="card">
        <h3 className="font-medium text-gray-900 mb-3">Role Permissions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
            <Shield className="text-purple-600 flex-shrink-0" size={20} />
            <div>
              <p className="font-medium text-gray-900">Owner</p>
              <p className="text-sm text-gray-600">
                Full access including user deletion and system settings
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
            <UserCheck className="text-blue-600 flex-shrink-0" size={20} />
            <div>
              <p className="font-medium text-gray-900">Admin</p>
              <p className="text-sm text-gray-600">
                Manage users, configure mappings and rules, view all reports
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
            <UserX className="text-gray-600 flex-shrink-0" size={20} />
            <div>
              <p className="font-medium text-gray-900">Rep</p>
              <p className="text-sm text-gray-600">
                View companies, see assigned projects, view own reports
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Delete confirmation modal */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900">Delete User</h3>
            <p className="mt-2 text-gray-600">
              Are you sure you want to delete this user? This action cannot be undone.
            </p>
            <div className="mt-6 flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirmId(null)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirmId)}
                disabled={deleteUserMutation.isPending}
                className="btn btn-danger"
              >
                {deleteUserMutation.isPending ? (
                  <>
                    <RefreshCw className="animate-spin" size={18} />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={18} />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invite user modal */}
      {showInviteModal && (
        <InviteUserModal onClose={() => setShowInviteModal(false)} />
      )}

      <WelcomeModal
        pageName={usersPageTour.pageName}
        onStartTour={startTour}
        onSkip={skipTour}
        onGlobalOptOut={globalOptOut}
        open={showWelcomeModal}
      />
    </div>
  );
}

// ===========================================
// Invite User Modal Component
// ===========================================

interface InviteUserModalProps {
  onClose: () => void;
}

function InviteUserModal({ onClose }: InviteUserModalProps) {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<'admin' | 'rep'>('rep');
  const inviteUserMutation = useInviteUser();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await inviteUserMutation.mutateAsync({
        email,
        displayName,
        role,
      });
      onClose();
    } catch {
      // Error handled by mutation
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold text-gray-900">Invite New User</h3>
        <p className="mt-1 text-sm text-gray-600">
          Send an invitation to add a new team member.
        </p>

        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Address *
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="input"
              placeholder="user@example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Display Name *
            </label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
              className="input"
              placeholder="John Smith"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role *
            </label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as 'admin' | 'rep')}
              className="input"
            >
              <option value="rep">Sales Rep</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary flex-1"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={inviteUserMutation.isPending}
              className="btn btn-primary flex-1"
            >
              {inviteUserMutation.isPending ? (
                <>
                  <RefreshCw className="animate-spin" size={18} />
                  Sending...
                </>
              ) : (
                <>
                  <Mail size={18} />
                  Send Invite
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
